'use client';

import type { FC } from 'react';
import { memo, useRef } from 'react';
import { cn } from '../../utils/cn';
import { modeOf } from '../caseBattle/battleModel';
import { cardShowState } from '../caseBattle/cardShowState';
import type { CaseBattleListItemResponse, CaseBattleHistoryItemResponse } from '../../services/generated/Api';
import { CardHeader } from './CardHeader';
import { CardFooter } from './CardFooter';
import { CaseStrip } from './CaseStrip';
import { DropFlight } from './DropFlight';

export type BattleData = CaseBattleListItemResponse | CaseBattleHistoryItemResponse;

interface BattleCardProps {
  battle: BattleData;
  onOpen?: (battleId: string) => void;
  onJoin?: (battleId: string) => void;
  now: number;
  variant?: 'lobby' | 'history';
}

const PREMIUM_COST_THRESHOLD = 500_000;

const MODE_TINT = {
  classic: 'bg-[radial-gradient(120%_100%_at_0%_0%,rgba(0,145,255,0.22),transparent_60%)]',
  crazy: 'bg-[radial-gradient(120%_100%_at_0%_0%,rgba(219,52,242,0.26),transparent_60%)]',
  sharing: 'bg-[radial-gradient(120%_100%_at_0%_0%,rgba(48,209,88,0.22),transparent_60%)]',
  last_case: 'bg-[radial-gradient(120%_100%_at_0%_0%,rgba(255,146,48,0.26),transparent_60%)]',
} as const;

export const BattleCard: FC<BattleCardProps> = memo(function BattleCard({ battle, onOpen, onJoin, now, variant = 'lobby' }: BattleCardProps) {
  const show = cardShowState(battle, now, variant === 'lobby');
  const { running, flight, flightColor, flightRoundId, flightElapsed } = show;
  const isPremium = battle.totalCost >= PREMIUM_COST_THRESHOLD;
  const cardRef = useRef<HTMLDivElement>(null);
  const currentCaseRef = useRef<HTMLSpanElement>(null);
  const catcherRef = useRef<HTMLSpanElement>(null);

  const card = (
    <div
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen ? () => onOpen(battle.id) : undefined}
      onKeyDown={onOpen ? (e) => { if (e.key === 'Enter') onOpen(battle.id); } : undefined}
      className={cn(
        'relative w-full text-start rounded-3xl bg-bg-secondary p-3 flex flex-col gap-2.5 overflow-hidden transition-shadow duration-500',
        onOpen && 'cursor-pointer',
        battle.mine && 'ring-1 ring-accent-blue/40',
        running && !flight && 'ring-1 ring-accent-green/35 shadow-[0_0_24px_-10px_rgba(48,209,88,0.6)]',
        running && flight && 'ring-2',
      )}
      style={flight ? { '--tw-ring-color': flightColor, boxShadow: `0 0 36px -6px ${flightColor}` } : undefined}
    >
      <div aria-hidden className={cn('pointer-events-none absolute inset-x-0 top-0 h-28 opacity-60', MODE_TINT[modeOf(battle.mode)])} />
      <CardHeader battle={battle} running={running} currentRound={show.currentRound} />
      <CaseStrip rounds={battle.rounds} currentRound={show.currentRound} dropColor={flight ? flightColor : null} currentCaseRef={currentCaseRef} />
      <CardFooter battle={battle} show={show} catcherRef={catcherRef} onOpen={onOpen} onJoin={onJoin} />
    </div>
  );

  const framed = isPremium ? (
    <div className="relative rounded-3xl p-[1.5px] shot-battle-conic-ring shadow-[0_0_28px_-8px_rgba(255,214,0,0.55)]">{card}</div>
  ) : (
    card
  );

  return (
    <div ref={cardRef} className={cn('relative', flight && 'z-10')}>
      {framed}
      {flight && flightRoundId && (
        <DropFlight
          key={flightRoundId}
          hashName={flight.hashName}
          imageUrl={flight.imageUrl ?? null}
          color={flightColor}
          fromRef={currentCaseRef}
          toRef={catcherRef}
          containerRef={cardRef}
          elapsedMs={flightElapsed}
        />
      )}
    </div>
  );
});
